import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Hexagon, Mail, ArrowLeft, CheckCircle2, KeyRound } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const HERO_VIDEO_URL = "/pitch/hero.mp4";

// ---------------------------------------------------------------------------
// Public forgot-password page (admin-mediated, no email). The user submits
// their email; we record a reset request the advisor resolves from the Admin
// Panel. The response is always generic to avoid account enumeration.
// ---------------------------------------------------------------------------

export default function ForgotPasswordPage() {
  useDocumentTitle("OmniDraftLab · Reset password");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch {
      // Still show the generic success state — never reveal whether the
      // account exists.
      setDone(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#04111A] text-1 relative overflow-hidden grid place-items-center px-5" data-testid="forgot-password-page">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <video className="absolute inset-0 w-full h-full object-cover opacity-30" autoPlay loop muted playsInline poster="/pitch/hero-poster.jpg">
          <source src="/pitch/hero.webm" type="video/webm" />
          <source src={HERO_VIDEO_URL} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-[#04111A]/80" />
      </div>

      <header className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/" className="flex items-center gap-2.5" data-testid="forgot-logo">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 grid place-items-center shadow-[0_8px_24px_-8px_rgba(34,211,238,0.6)]">
            <Hexagon className="h-4 w-4 text-[#04111A]" strokeWidth={2.4} />
          </div>
          <span className="font-semibold tracking-tight text-1">OmniDraftLab</span>
        </Link>
        <Link to="/login" data-testid="forgot-back-login" className="text-2 hover:text-1 text-sm flex items-center gap-1.5 transition">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
      </header>

      <motion.div
        initial={{ opacity: 0, y: 22 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 0.65, 0.2, 1] }}
        className="relative z-10 w-full max-w-md glass-strong rounded-3xl p-7 sm:p-9"
      >
        <div className="h-11 w-11 rounded-2xl bg-cyan-300/15 ring-1 ring-cyan-300/30 grid place-items-center mb-5">
          <KeyRound className="h-5 w-5 text-cyan-300" />
        </div>

        {done ? (
          <div data-testid="forgot-success">
            <div className="flex items-center gap-2 text-emerald-300">
              <CheckCircle2 className="h-5 w-5" />
              <h1 className="text-xl font-semibold text-1">Request received</h1>
            </div>
            <p className="text-2 text-sm mt-3 leading-relaxed">
              If an account exists for <span className="text-1 font-medium">{email}</span>, your advisor has been notified and will reset your password. You'll receive a new temporary password from them, and you'll be prompted to set your own on your next sign-in.
            </p>
            <Button onClick={() => navigate("/login")} data-testid="forgot-return-login" className="mt-6 w-full h-11 bg-cyan-300 text-[#04111A] hover:bg-cyan-200 font-semibold rounded-2xl">
              Return to sign in
            </Button>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-semibold tracking-tight text-1">Forgot your password?</h1>
            <p className="text-2 text-sm mt-2 leading-relaxed">
              Enter your email and your advisor will be notified to reset it for you. (For security, password resets are handled directly by your advisor.)
            </p>
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="forgot-email" className="text-2 text-xs">Email</Label>
                <div className="relative">
                  <Mail className="h-4 w-4 text-3 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    id="forgot-email"
                    data-testid="forgot-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@example.com"
                    className="pl-9 h-11 bg-white/5 border-white/10 text-1"
                  />
                </div>
              </div>
              <Button type="submit" disabled={busy} data-testid="forgot-submit" className="w-full h-11 bg-cyan-300 text-[#04111A] hover:bg-cyan-200 font-semibold rounded-2xl">
                {busy ? "Sending…" : "Request password reset"}
              </Button>
            </form>
          </>
        )}
      </motion.div>

      <Toaster richColors theme="dark" position="top-right" />
    </div>
  );
}
